import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Star, ChevronsUpDown } from "lucide-react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { apiError } from "../lib/api";
import { createItem, updateItem } from "../hooks/useEntity";
import { formatPhone } from "../lib/utils-format";
import { toast } from "sonner";

export const contactName = (c) => [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "Contact";

function CompanyCombobox({ companies, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sel = companies.find((c) => c.id === value);
  const label = sel ? (sel.company_name || contactName(sel)) : "Select a company…";
  const filt = companies.filter((c) => (c.company_name || contactName(c)).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative">
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} data-testid="contact-field-company"
        className="w-full flex items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm disabled:opacity-50">
        <span className={sel ? "" : "text-muted-foreground"}>{label}</span>
        <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
      </button>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md">
          <div className="p-2 border-b border-border">
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies…" className="h-8" data-testid="contact-company-search" />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filt.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No matches</div>}
            {filt.map((c) => (
              <button type="button" key={c.id} onClick={() => { onChange(c.id); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent ${c.id === value ? "bg-accent" : ""}`} data-testid={`contact-company-opt-${c.id}`}>
                {c.company_name || contactName(c)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ContactField = ({ k, label, type = "text", form, set }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
    <Input type={type} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`contact-field-${k}`} />
  </div>
);

export function ContactForm({ open, onOpenChange, initial, companies, onSaved, lockedCompanyId }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ company_id: lockedCompanyId || "", ...(initial || {}) });
  }, [open, initial, lockedCompanyId]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name && !form.last_name) { toast.error("Enter a contact name"); return; }
    setSaving(true);
    try {
      if (initial?.id) { await updateItem("partner_contacts", initial.id, form); toast.success("Contact updated"); }
      else { await createItem("partner_contacts", form); toast.success("Contact added"); }
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{initial?.id ? "Edit Contact" : "New Contact"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3"><ContactField k="first_name" label="First Name" form={form} set={set} /><ContactField k="last_name" label="Last Name" form={form} set={set} /></div>
          <ContactField k="title" label="Title / Role" form={form} set={set} />
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Company</Label>
            <CompanyCombobox companies={companies} value={form.company_id || ""} onChange={(v) => set("company_id", v)} disabled={!!lockedCompanyId} />
          </div>
          <div className="grid grid-cols-2 gap-3"><ContactField k="email" label="Email" form={form} set={set} /><ContactField k="mobile_phone" label="Mobile Phone" form={form} set={set} /></div>
          <div className="grid grid-cols-2 gap-3">
            <ContactField k="business_phone" label="Business Phone" form={form} set={set} />
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Primary Contact</Label>
              <Select value={form.is_primary ? "yes" : "no"} onValueChange={(v) => set("is_primary", v === "yes")}>
                <SelectTrigger data-testid="contact-field-primary"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="contact-save-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PartnerContacts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/partner_contacts?limit=500"),
      api.get("/directory?limit=500"),
    ]).then(([c, d]) => {
      setItems(c.data.items || []);
      setCompanies(d.data.items || []);
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const companyName = (id) => { const c = companies.find((x) => x.id === id); return c ? (c.company_name || "—") : "—"; };
  const q = search.trim().toLowerCase();
  const filtered = items.filter((c) => !q || contactName(c).toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || companyName(c.company_id).toLowerCase().includes(q));

  const cols = [
    { key: "name", label: "Name", render: (c) => (
      <span className="font-medium flex items-center gap-1.5">{contactName(c)}{c.is_primary && <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />}</span>
    ) },
    { key: "title", label: "Title", render: (c) => c.title || "—" },
    { key: "company", label: "Company", render: (c) => c.company_id ? (
      <button onClick={(e) => { e.stopPropagation(); navigate(`/directory/${c.company_id}`); }} className="text-primary hover:underline">{companyName(c.company_id)}</button>
    ) : "—" },
    { key: "email", label: "Email", render: (c) => c.email || "—" },
    { key: "phone", label: "Phone", render: (c) => formatPhone(c.mobile_phone || c.business_phone) || "—" },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <PageHeader title="Contacts" subtitle="People linked to your partner companies"
        actions={<Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="contact-new-btn"><Plus className="h-4 w-4 mr-1.5" />New Contact</Button>} />
      <div className="max-w-sm mb-4">
        <Input placeholder="Search contacts…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contacts-search" />
      </div>
      <DataTable columns={cols} items={filtered} loading={loading} emptyLabel="No contacts yet"
        onRowClick={(c) => { setEditing(c); setOpen(true); }} />
      <ContactForm open={open} onOpenChange={setOpen} initial={editing} companies={companies} onSaved={load} />
    </div>
  );
}
